import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Package2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { eur } from '@/lib/format';
import { useCart } from '@/lib/cart';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/Header';
import Seo from '@/components/Seo';

export default function MenuPage() {
  const [bowls, setBowls] = useState([]);
  const [beverages, setBeverages] = useState([]);
  const [customItems, setCustomItems] = useState({});  // { categoryKey: [items] }
  const [combos, setCombos] = useState([]);
  const [labels, setLabels] = useState({});
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [selectedDrinkId, setSelectedDrinkId] = useState('');
  const add = useCart(s => s.add);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/signature-bowls').then(r => setBowls(r.data));
    api.get('/ingredients?category=beverage').then(r => setBeverages(r.data));
    api.get('/combos').then(r => setCombos(r.data)).catch(() => {});
    api.get('/settings').then(r => setLabels(r.data?.category_labels || {})).catch(() => {});
    // Fetch all ingredients then group by custom categories (not in built-in set)
    const BUILTIN = new Set(['size', 'base', 'protein', 'topping', 'sauce', 'extra', 'beverage']);
    api.get('/ingredients').then(r => {
      const groups = {};
      (r.data || []).forEach(i => {
        if (!BUILTIN.has(i.category) && i.active !== false) {
          (groups[i.category] = groups[i.category] || []).push(i);
        }
      });
      setCustomItems(groups);
    }).catch(() => {});
  }, []);

  const addBeverage = (b) => {
    add({
      kind: 'signature',
      signature_id: b.id,
      name: b.name,
      unit_price: b.price,
      quantity: 1,
      components_summary: b.description ? [b.description] : [],
    });
    toast.success(`${b.name} aggiunto al carrello`);
  };

  const openCombo = (c) => {
    // Combo poke da comporre → apri il Builder in modalità combo
    if ((c.kind || 'custom') === 'custom' && c.size_id) {
      nav(`/builder?combo=${c.id}`);
      return;
    }
    // Combo poke firmata → dialog scelta bevanda immediata
    setSelectedCombo(c);
    setSelectedDrinkId(c.drink_ids[0] || '');
  };

  const confirmCombo = () => {
    if (!selectedCombo) return;
    if (!selectedDrinkId) { toast.error('Seleziona una bevanda'); return; }
    const sig = bowls.find(b => b.id === selectedCombo.signature_bowl_id);
    const drink = beverages.find(d => d.id === selectedDrinkId);
    const summary = [];
    if (sig) summary.push(`Poke: ${sig.name}`);
    if (drink) summary.push(`Bevanda: ${drink.name}`);
    add({
      kind: 'combo',
      combo_id: selectedCombo.id,
      signature_id: sig?.id,
      drink_id: drink?.id,
      name: selectedCombo.name,
      unit_price: selectedCombo.price,
      quantity: 1,
      components_summary: summary,
    });
    toast.success(`${selectedCombo.name} aggiunto al carrello`);
    setSelectedCombo(null);
  };

  const activeCombos = combos.filter(c => c.active !== false);

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Menu — Poke Bowl Firmate a Catania"
        description="Scopri le poke bowl firmate MōMA a Catania: ricette selezionate con pesce fresco, verdure croccanti e salse originali. Ordina online, consegna a domicilio o ritiro."
        path="/menu"
        keywords="menu poke catania, poke firmate, poke bowl pesce, MōMA menu"
      />
      <PublicHeader />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-10 w-full">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Menu</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Le nostre poke</h1>
        </div>

        <Link to="/builder" data-testid="menu-build-cta">
          <div className="rounded-2xl bg-[--brand-primary] text-white p-5 mb-8 flex items-center gap-4 shadow-[var(--shadow-md)] hover:opacity-95">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center"><Plus size={22}/></div>
            <div className="flex-1">
              <div className="font-display text-xl font-bold">Crea la tua Poke</div>
              <div className="text-sm text-white/80">Componi passo dopo passo il tuo bowl personalizzato</div>
            </div>
            <Button className="rounded-xl bg-white hover:bg-white/90 text-[--brand-primary]">Inizia</Button>
          </div>
        </Link>

        {activeCombos.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <Package2 size={18} className="text-[--brand-accent]"/>
              <div>
                <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Offerte speciali</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold">Menù combo</h2>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {activeCombos.map((c) => {
                const sig = bowls.find(b => b.id === c.signature_bowl_id);
                return (
                  <motion.div key={c.id}
                    initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    className="rounded-2xl bg-white border border-[--brand-border] overflow-hidden shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow flex flex-col"
                    data-testid="menu-combo-card">
                    <div className="aspect-[4/3] overflow-hidden relative bg-[--brand-surface-2]">
                      {c.image_url ? (
                        <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : sig?.image_url ? (
                        <img src={sig.image_url} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[--brand-muted]"><Package2 size={40}/></div>
                      )}
                      <Badge className="absolute top-2 left-2 bg-[--brand-accent] text-white border-transparent">Combo</Badge>
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display text-lg font-bold">{c.name}</h3>
                        <div className="tabular-nums font-semibold text-[--brand-primary]">{eur(c.price)}</div>
                      </div>
                      {c.description && <p className="text-sm text-[--brand-muted] mt-1 line-clamp-2">{c.description}</p>}
                      <div className="text-xs text-[--brand-muted] mt-2">
                        {sig ? `🥗 ${sig.name}` : ''}
                        {c.drink_ids?.length ? ` · 🥤 ${c.drink_ids.length} bevande a scelta` : ''}
                      </div>
                      <Button className="mt-3 rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] w-full"
                              onClick={() => openCombo(c)}
                              data-testid="menu-combo-add-button">
                        <Plus size={16} className="mr-1"/> Scegli bevanda
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bowls.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.04 }}
              className="rounded-2xl bg-white border border-[--brand-border] overflow-hidden shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow flex flex-col"
              data-testid="menu-bowl-card"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" loading="lazy" />
                {b.featured && <Badge className="absolute top-2 left-2 bg-[--brand-accent] text-white border-transparent">Più venduta</Badge>}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-bold">{b.name}</h3>
                  <div className="tabular-nums font-semibold text-[--brand-primary]">{eur(b.price)}</div>
                </div>
                {b.tags && b.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1" data-testid="menu-bowl-tags">
                    {b.tags.map((t, i) => (
                      <Badge key={i} className="bg-[--brand-primary]/10 text-[--brand-primary] border-[--brand-primary]/30 text-[10px] font-semibold">{t}</Badge>
                    ))}
                  </div>
                )}
                <p className="text-sm text-[--brand-muted] mt-1 line-clamp-3 flex-1">{b.description}</p>
                <Button
                  className="mt-3 rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] w-full"
                  data-testid="menu-add-to-cart-button"
                  onClick={() => {
                    add({
                      kind: 'signature',
                      signature_id: b.id,
                      name: b.name,
                      unit_price: b.price,
                      quantity: 1,
                      components_summary: [b.description],
                    });
                    toast.success(`${b.name} aggiunta al carrello`);
                  }}
                ><Plus size={16} className="mr-1"/> Aggiungi</Button>
              </div>
            </motion.div>
          ))}
        </div>

        {beverages.length > 0 && (
          <section className="mt-12">
            <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Bevande</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">Da bere</h2>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="menu-beverages-grid">
              {beverages.map(b => (
                <div key={b.id} className="rounded-2xl bg-white border border-[--brand-border] p-3 shadow-[var(--shadow-sm)] flex flex-col" data-testid="menu-beverage-card">
                  {b.image_url && (
                    <div className="aspect-square overflow-hidden rounded-xl bg-[--brand-surface-2] mb-2">
                      <img src={b.image_url} alt={b.name} className="w-full h-full object-cover" loading="lazy"/>
                    </div>
                  )}
                  <div className="font-semibold text-sm">{b.name}</div>
                  {b.description && <div className="text-xs text-[--brand-muted] line-clamp-2 mt-0.5">{b.description}</div>}
                  <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                    <div className="tabular-nums text-sm font-bold text-[--brand-primary]">{eur(b.price)}</div>
                    <Button size="sm" className="rounded-lg bg-[--brand-primary] hover:bg-[--brand-primary-hover] h-8 px-2" onClick={() => addBeverage(b)} data-testid="menu-add-beverage-button">
                      <Plus size={14}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {Object.entries(customItems).map(([catKey, items]) => (
          <section key={catKey} className="mt-12" data-testid={`menu-custom-section-${catKey}`}>
            <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Sezione</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">{labels[catKey] || catKey.charAt(0).toUpperCase() + catKey.slice(1)}</h2>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {items.sort((a,b)=>a.sort_order-b.sort_order).map(i => (
                <div key={i.id} className="rounded-2xl bg-white border border-[--brand-border] p-3 shadow-[var(--shadow-sm)] flex flex-col" data-testid="menu-custom-item">
                  {i.image_url && (
                    <div className="aspect-square overflow-hidden rounded-xl bg-[--brand-surface-2] mb-2">
                      <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" loading="lazy"/>
                    </div>
                  )}
                  <div className="font-semibold text-sm">{i.name}</div>
                  {i.description && <div className="text-xs text-[--brand-muted] line-clamp-2 mt-0.5">{i.description}</div>}
                  <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                    <div className="tabular-nums text-sm font-bold text-[--brand-primary]">{eur(i.price)}</div>
                    <Button size="sm" className="rounded-lg bg-[--brand-primary] hover:bg-[--brand-primary-hover] h-8 px-2"
                            onClick={() => {
                              add({ kind: 'signature', signature_id: i.id, name: i.name, unit_price: i.price, quantity: 1, components_summary: i.description ? [i.description] : [] });
                              toast.success(`${i.name} aggiunto`);
                            }}
                            data-testid="menu-add-custom-button">
                      <Plus size={14}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>
      <Footer />

      {/* Combo drink selection dialog */}
      <Dialog open={!!selectedCombo} onOpenChange={(o) => !o && setSelectedCombo(null)}>
        <DialogContent className="max-w-md" data-testid="combo-select-dialog">
          <DialogHeader>
            <DialogTitle>Scegli la bevanda</DialogTitle>
            <DialogDescription>Menù "{selectedCombo?.name}" · {selectedCombo && eur(selectedCombo.price)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(selectedCombo?.drink_ids || []).map(id => {
              const d = beverages.find(x => x.id === id);
              if (!d) return null;
              const on = selectedDrinkId === id;
              return (
                <label key={id}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${on ? 'border-[--brand-primary] bg-[#F3FBF7]' : 'border-[--brand-border] bg-white hover:bg-[--brand-surface-2]'}`}
                  data-testid={`combo-drink-choice-${id}`}>
                  <input type="radio" checked={on} onChange={() => setSelectedDrinkId(id)} className="accent-[--brand-primary]"/>
                  {d.image_url && <img src={d.image_url} alt={d.name} className="w-12 h-12 rounded-lg object-cover"/>}
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{d.name}</div>
                    {d.description && <div className="text-xs text-[--brand-muted]">{d.description}</div>}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelectedCombo(null)}>Annulla</Button>
            <Button onClick={confirmCombo} disabled={!selectedDrinkId} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="combo-select-confirm">
              <Plus size={14} className="mr-1"/>Aggiungi al carrello
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
